import { FileConfig } from './../model/file-config';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { IAsideRightOptions, IPill, ITag, Status, IToast, ToastService } from 'codex-lib';
import { NewEnvelopeService } from '../new-envelope/new-envelope.service';
import { InteractService } from '../services/interact.service';
import { FileValidateService } from '../services/file-validate.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EnvelopeFile } from 'src/app/main-envelope/model/envelope-file.model';
import { FileUtil } from 'src/app/util/file-utils';
import { Subscription } from 'rxjs';
import { BrowserUtil } from 'src/app/util/browser-util';
import { TranslateService } from '@ngx-translate/core';
import { TrackerData } from 'src/app/modules/google-analytics/model/tracker-data.model';
import { AnalyticEventClient } from 'src/app/enum/analytic-event-client.enum';
import { AnalyticsService } from 'src/app/modules/google-analytics/service/analytics.service';
import { CodexComponentColor } from 'src/app/util/codex-component-color';

@Component({
  selector: 'app-envelope-sidebar-detail',
  templateUrl: './envelope-sidebar-detail.component.html',
  styleUrls: ['./envelope-sidebar-detail.component.scss']
})

export class EnvelopeSidebarDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() closeAside = new EventEmitter<void>();
  @ViewChild('thumbnailContainer') thumbnailContainer: ElementRef<HTMLDivElement>;

  activeModal: NgbActiveModal;
  includeRubricsSubscription: Subscription;
  pageSelectedSubscription: Subscription;
  fileSelectedSubscription: Subscription;

  private initializedFiles = new Set<string>();
  private selectedPageByFile = new Map<string, string>();

  rightOptions: IAsideRightOptions = {
    overlapContent: false,
    title: 'details',
    icon: {
      iconName: 'info-circle',
      prefix: 'fad'
    }
  };

  completedTag: ITag = {
    description: 'tag.completed',
    tooltip: 'tag.completed',
    type: Status.SUCCESS,
    icon: {
      iconName: 'check-circle',
      prefix: 'far',
    }
  };

  warningTag: ITag = {
    description: 'tag.pending',
    tooltip: 'tag.pending',
    type: Status.DANGER,
    icon: {
      iconName: 'exclamation-triangle',
      prefix: 'far',
    }
  };

  isMobile: boolean = BrowserUtil.isMobile();
  themePrimaryColor = '#1577b8';

  constructor(
    private fileValidateService: FileValidateService,
    public newEnvelopeService: NewEnvelopeService,
    public interactService: InteractService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private googleAnalitycsService: AnalyticsService) {

    this.includeRubricsSubscription = this.newEnvelopeService
      .getSubjectIncludeRubrics().subscribe((x) => {
        this.newEnvelopeService.includeRubrics = x;
      });
  }

  ngOnInit(): void {
    this.themePrimaryColor = CodexComponentColor.getPrimaryColor();

    this.pageSelectedSubscription = this.newEnvelopeService
      .getSubjectPageSelected()
      .subscribe((page) => {
        this.onPageSelectedFromViewer(page);
      });

    this.fileSelectedSubscription = this.newEnvelopeService
      .getSubjectfileSelected()
      .subscribe(() => {
        setTimeout(() => this.syncThumbnailStateForCurrentFile(), 0);
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.syncThumbnailStateForCurrentFile(), 0);
  }

  ngOnDestroy(): void {
    this.includeRubricsSubscription?.unsubscribe();
    this.pageSelectedSubscription?.unsubscribe();
    this.fileSelectedSubscription?.unsubscribe();
  }

  asideRight() {
    this.closeAside.emit();
  }

  disabledEditMode() {
    if (this.interactService.lockDrag) {
      this.newEnvelopeService.deactivateEditMode();
      return true;
    }
    return false;
  }

  onChangeEditMode(event) {
    this.newEnvelopeService.editMode = event;
    if (this.newEnvelopeService.editMode) {
      this.newEnvelopeService.activateEditMode();
    } else {
      this.newEnvelopeService.deactivateEditMode();
    }
  }

  itemRadioSelected(envelopeFileSelected: EnvelopeFile) {
    let currentFile = this.newEnvelopeService.envelopeFiles.find(x => x.checked === true);

    if (currentFile && currentFile.name === envelopeFileSelected.name) return;

    var validateResult = this.newEnvelopeService.validateSignatoresOnFileByFileName(currentFile.name);
    const isvalidStep = validateResult.isValidStamps && 
      validateResult.isValidForm && 
      validateResult.isValidRubrics;

    if (currentFile && !isvalidStep) {
      if (!validateResult.isValidStamps || !validateResult.isValidRubrics) {
        this.buildToastInvalidStep('envelope.main-envelope.toast.incompleteDocument');
        return;
      }

      if (!validateResult.isValidForm) {
        this.buildToastInvalidStep('envelope.main-envelope.toast.overlappingFields');
        return;
      }
    }

    this.newEnvelopeService.envelopeFiles.map(x => x.checked = false);
    envelopeFileSelected.checked = true;
    let fileSelected = this.newEnvelopeService.modelData[0].filter(x => x.name == envelopeFileSelected.name).shift();
    if (fileSelected !== null && fileSelected) {
      this.selectAndLoadFile(fileSelected);
    }
  }

  selectAndLoadFile(envelopeFile: File) {
    this.newEnvelopeService.setSubjectfileSelected(envelopeFile);
  }

  pagesEnvelopeFileSelected(): FileConfig[] {
    const envelopeFileSelected = this.newEnvelopeService.envelopeFiles
      .filter((x) => x.checked === true)
      .shift();

    if (envelopeFileSelected) {
      return this.fileValidateService.fileConfig.filter(
        (x) => x.fileName === FileUtil.removeExt(envelopeFileSelected.name)
      );
    }

    return [];
  }

  selectedPage(fileConfig: FileConfig) {
    this.applyPageSelection(fileConfig.page, true);
    this.scrollSelectedIntoView(fileConfig.page);
  }

  onChangeIncludeSignature(event) {
    this.newEnvelopeService.includeSignature = event;
    if (this.newEnvelopeService.includeSignature) {
      this.newEnvelopeService.includeSignatureMode();
    } else {
      this.newEnvelopeService.excludeSignatureMode();
    }
  }

  onChangeIncludeRubric(event) {
    this.googleAnalitycsService.sendEvent(
      new TrackerData(
        AnalyticEventClient.TK_PT_SIGNER_NEW_ENVELOPE,
        'Signer_NovEnv_RubricasAutomaticas',
        event ? 'Ativo' : 'Inativo'
      )
    );

    this.newEnvelopeService.includeRubrics = event;
    if (this.newEnvelopeService.includeRubrics) {
      this.newEnvelopeService.includeRubricMode();
    } else {
      this.newEnvelopeService.excludeRubricMode();
    }
  }

  handleToggleClickRubric(event: Event) {
    if (this.newEnvelopeService.envelopeFiles.some(x => x.upload == true && x.fileAlreadySigned)) {
      event.preventDefault();
      event.stopPropagation();
      this.toastFilePreviouslySigned('rubric');
    }
  }
  toastFilePreviouslySigned(type: 'rubric' | 'signature') {
    const toast: IToast = {
      text: this.translateService.instant(type === 'signature' ?
         'businessError.twoOrMoreStampsPreviouslySignedDoc' : 
         'businessError.rubricPreviouslySignedDoc'),
      autoHide: true,
      type: 'warning'
    };
    this.toastService.createNewToast(toast);
  }

  loadSignature() {
    return this.interactService.loadSignature ? true : false;
  }

  pill: IPill = {
    icon: {
      iconName: 'exclamation-circle',
      prefix: 'fal',
    },
    description: 'envelope.main-envelope.asideRight.pill-miniature',
    type: Status.WARNING
  };

  insertPill(page: string) {
    const fileName = FileUtil.removeExt(this.newEnvelopeService.envelopeFiles.find(x => x.checked == true).name);
    let rubric = this.interactService.rubricStamps.some(x => x.page == page && x.fileName == fileName);
    let signature = this.interactService.signatoryStamps.some(x => x.signaturePositionPage == page && x.fileName == fileName);

    return rubric || signature;
  }

  checkStatusStampsOfSignaturesByFileName(fileName: any) {
    var result = this.newEnvelopeService.validateSignatoresOnFileByFileName(fileName);
    return ((result?.isValidStamps ?? false) && (result?.isValidRubrics ?? false));
  }

  private onPageSelectedFromViewer(page: string): void {
    if (!page) {
      return;
    }

    const currentPages = this.pagesEnvelopeFileSelected();
    if (!currentPages.length) {
      return;
    }

    const normalizedPage = page.toString();
    const pageExistsOnCurrentFile = currentPages.some((item) => item.page === normalizedPage);
    if (!pageExistsOnCurrentFile) {
      return;
    }

    this.applyPageSelection(normalizedPage, false);
    this.scrollSelectedIntoView(normalizedPage);
  }

  private syncThumbnailStateForCurrentFile(): void {
    const pages = this.pagesEnvelopeFileSelected();
    if (!pages.length) {
      return;
    }

    const currentFileName = this.getCurrentFileName();
    if (!currentFileName) {
      return;
    }

    const savedPage = this.selectedPageByFile.get(currentFileName);
    const selectedPageInFile = pages.find((item) => item.selected)?.page;
    const fallbackLastPage = pages[pages.length - 1].page;
    const targetPage = savedPage || selectedPageInFile || fallbackLastPage;

    this.applyPageSelection(targetPage, false);

    const firstRenderForFile = !this.initializedFiles.has(currentFileName);
    if (firstRenderForFile) {
      this.initializedFiles.add(currentFileName);
      this.scrollToBottom();
      return;
    }

    this.scrollSelectedIntoView(targetPage);
  }

  private applyPageSelection(page: string, emitPageSelection: boolean): void {
    const pages = this.pagesEnvelopeFileSelected();
    if (!pages.length) {
      return;
    }

    const requestedPage = page.toString();
    const selectedPage =
      pages.find((item) => item.page === requestedPage)?.page ??
      pages[pages.length - 1].page;

    this.fileValidateService.fileConfig.map((item) => (item.selected = false));
    const selectedConfig = pages.find((item) => item.page === selectedPage);
    if (!selectedConfig) {
      return;
    }

    selectedConfig.selected = true;
    const currentFileName = this.getCurrentFileName();
    if (currentFileName) {
      this.selectedPageByFile.set(currentFileName, selectedPage);
    }

    if (emitPageSelection) {
      this.newEnvelopeService.setSubjectPageSelected(selectedPage);
    }
  }

  private getCurrentFileName(): string {
    const currentFile = this.newEnvelopeService.envelopeFiles.find((item) => item.checked === true);
    return currentFile ? FileUtil.removeExt(currentFile.name) : '';
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const container = this.thumbnailContainer?.nativeElement;
      if (!container) {
        return;
      }

      container.scrollTop = container.scrollHeight;
    }, 0);
  }

  private scrollSelectedIntoView(page: string): void {
    setTimeout(() => {
      const container = this.thumbnailContainer?.nativeElement;
      if (!container) {
        return;
      }

      const selectedElement = container.querySelector(
        `[data-page="${page}"]`
      ) as HTMLElement;

      if (!selectedElement) {
        return;
      }

      const scrollTop =
        selectedElement.offsetTop -
        container.clientHeight / 2 +
        selectedElement.clientHeight / 2;

      container.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: 'smooth',
      });
    }, 0);
  }

  private buildToastInvalidStep(message : string) {
    const toast: IToast = {
      text: this.translateService.instant(message),
      autoHide: true,
      type: 'warning'
    };
    this.toastService.createNewToast(toast);
  }
}
